<template>
  <HrPageLayout
    title="Vacancy Management"
    subtitle="Create and manage job vacancies"
    icon="📢"
    :breadcrumb-items="[{ label: 'Recruitment' }, { label: 'Vacancies' }]"
  >
    <template #header-actions>
      <div class="hr-flex hr-flex-center hr-gap-2">
        <select class="hr-select" style="width: 140px;" v-model="statusFilter" @change="page=1; fetchVacancies()">
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="closed">Closed</option>
        </select>
        <button class="hr-btn hr-btn-secondary" @click="exportVacancyReport">CSV report</button>
        <button class="hr-btn hr-btn-primary" @click="exportVacancyPdf">PDF report</button>
        <button v-if="hasRight('VACANCY MANAGEMENT')" class="hr-btn hr-btn-success" @click="openAddModal">
          <i class="bx bx-plus-circle"></i> Add Vacancy
        </button>
      </div>
    </template>

    <div class="vacancies-content">
      <div class="hr-chart-card">
        <div class="hr-chart-header">
          <h5 class="hr-chart-title"><i class="bx bx-briefcase"></i> Vacancies</h5>
          <div class="hr-search-wrap" style="min-width: 260px;">
            <input v-model="searchValue" type="text" class="hr-input" placeholder="Search title, ref, department…" @keyup.enter="page=1; fetchVacancies()" />
            <i class="bx bx-search-alt search-icon" @click="page=1; fetchVacancies()"></i>
          </div>
        </div>
        <div class="hr-chart-body">
          <div v-if="loading" class="hr-loading-state">
            <div class="hr-spinner"></div>
            <p>Loading vacancies...</p>
          </div>
          <div v-else-if="vacancies.length === 0" class="hr-empty-state">
            <i class="bx bx-file"></i>
            <h5>No vacancies found</h5>
            <p>Create your first vacancy to start recruiting.</p>
            <button v-if="hasRight('VACANCY MANAGEMENT')" class="hr-btn hr-btn-primary" @click="openAddModal"><i class="bx bx-plus hr-me-2"></i> Add Vacancy</button>
          </div>
          <div v-else class="hr-table-wrapper">
            <table class="hr-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Title</th>
                  <th>Department</th>
                  <th>Closing Date</th>
                  <th style="text-align:center">Applicants</th>
                  <th style="text-align:center">Status</th>
                  <th style="text-align:center">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(v, i) in vacancies" :key="v.id">
                  <td>{{ (page - 1) * pageSize + i + 1 }}</td>
                  <td>
                    <div class="type-name">{{ v.title }}</div>
                    <small v-if="v.vacancy_ref_no" class="type-desc">{{ v.vacancy_ref_no }}</small>
                  </td>
                  <td>{{ v.department || '-' }}</td>
                  <td>{{ v.closingDate || '-' }}</td>
                  <td style="text-align:center">{{ v.applicants_count ?? 0 }}</td>
                  <td style="text-align:center">
                    <span class="hr-badge" :class="v.statusNormalized === 'active' ? 'hr-badge-success' : 'hr-badge-muted'">
                      {{ v.status }}
                    </span>
                  </td>
                  <td style="text-align:center">
                    <div class="hr-flex hr-flex-center hr-gap-2" style="justify-content: center; flex-wrap: wrap;">
                      <button v-if="hasRight('VACANCY MANAGEMENT')" class="hr-btn hr-btn-info hr-btn-sm" @click="openEditModal(v)" title="Edit">Edit</button>
                      <button v-if="hasRight('VACANCY MANAGEMENT') && v.statusNormalized === 'active'" class="hr-btn hr-btn-secondary hr-btn-sm" @click="closeVacancy(v)">Close</button>
                      <button v-if="hasRight('VACANCY MANAGEMENT') && v.statusNormalized !== 'active'" class="hr-btn hr-btn-success hr-btn-sm" @click="readvertiseVacancy(v)">Re-advertise</button>
                      <button class="hr-btn hr-btn-outline-primary hr-btn-sm" @click="copyShareLink(v)">Share link</button>
                      <button class="hr-btn hr-btn-secondary hr-btn-sm" @click="viewCandidates(v)">Candidates</button>
                      <button v-if="hasRight('VACANCY MANAGEMENT')" class="hr-btn hr-btn-danger hr-btn-sm" @click="confirmDelete(v)">Delete</button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
            <div class="vac-pagination" v-if="totalPages > 1">
              <button class="hr-btn hr-btn-sm hr-btn-secondary" :disabled="page <= 1" @click="page--; fetchVacancies()">Prev</button>
              <span>Page {{ page }} of {{ totalPages }} ({{ totalRecords }} total)</span>
              <button class="hr-btn hr-btn-sm hr-btn-secondary" :disabled="page >= totalPages" @click="page++; fetchVacancies()">Next</button>
            </div>
          </div>
        </div>
      </div>

      <!-- Add/Edit Vacancy Modal -->
      <ModalWrapper ref="formModal" :title="isEditing ? 'Edit Vacancy' : 'Add Vacancy'" size="large">
        <template #body>
          <form @submit.prevent="saveVacancy" class="vacancy-form">
            <!-- Job Details -->
            <div class="section-title">Job Details</div>
            <div class="hr-form-grid hr-mb-3">
              <div>
                <label class="hr-form-label">Job Title <span class="required">*</span></label>
                <input
                  type="text"
                  class="hr-input"
                  v-model="formData.title"
                  required
                  placeholder="Enter Job Title"
                />
              </div>
              <div>
                <label class="hr-form-label">Job Location <span class="required">*</span></label>
                <input
                  type="text"
                  class="hr-input"
                  v-model="formData.jobLocation"
                  required
                  placeholder="Enter Job Location"
                />
              </div>
            </div>

            <div class="hr-form-grid hr-mb-3">
              <div>
                <label class="hr-form-label">Department <span class="required">*</span></label>
                <div class="hr-flex hr-gap-2">
                  <select class="hr-select" v-model="formData.department" style="flex: 1;" @change="onVacancyDepartmentChange">
                    <option value="">Select Department</option>
                    <option v-for="dept in departments" :key="dept.id" :value="dept.departmentName">
                      {{ dept.departmentName }}
                    </option>
                  </select>
                  <button 
                    type="button" 
                    class="hr-btn hr-btn-secondary hr-btn-sm" 
                    @click="openAddDepartmentModal" 
                    title="Add New Department"
                    style="padding: 0 12px;"
                  >
                    <i class="bx bx-plus"></i>
                  </button>
                </div>
              </div>
              <div>
                <label class="hr-form-label">Section / Sub-vote</label>
                <select class="hr-select" v-model="formData.sectionId" :disabled="!formData.department" @change="onVacancySectionChange">
                  <option value="">{{ formData.department ? 'Select Section (optional)' : 'Select Department first' }}</option>
                  <option v-for="sec in vacancySections" :key="sec.id" :value="sec.id">
                    {{ sec.name }}{{ sec.code ? ' (' + sec.code + ')' : '' }}
                  </option>
                </select>
              </div>
              <div>
                <label class="hr-form-label">Unit / Directorate</label>
                <select class="hr-select" v-model="formData.unitId" :disabled="!formData.sectionId" @change="onVacancyUnitChange">
                  <option value="">{{ formData.sectionId ? 'Select Unit (optional)' : 'Select Section first' }}</option>
                  <option v-for="u in vacancyUnits" :key="u.id" :value="u.id">
                    {{ u.name }}{{ u.code ? ' (' + u.code + ')' : '' }}
                  </option>
                </select>
              </div>
              <div>
                <label class="hr-form-label">Sub-unit</label>
                <select class="hr-select" v-model="formData.subUnitId" :disabled="!formData.unitId">
                  <option value="">{{ formData.unitId ? 'Select Sub-unit (optional)' : 'Select Unit first' }}</option>
                  <option v-for="su in vacancySubUnits" :key="su.id" :value="su.id">
                    {{ su.name }}{{ su.code ? ' (' + su.code + ')' : '' }}
                  </option>
                </select>
              </div>
            </div>

            <div class="hr-form-grid hr-mb-3">
              <div>
                <label class="hr-form-label">Vacancy Reference Number <span class="required">*</span></label>
                <input
                  type="text"
                  class="hr-input"
                  v-model="formData.vacancyRefNo"
                  required
                  placeholder="Enter Reference Number"
                />
              </div>
              <div>
                <label class="hr-form-label">Advert Date <span class="required">*</span></label>
                <input
                  type="date"
                  class="hr-input"
                  v-model="formData.advertDate"
                  required
                />
              </div>
            </div>

            <div class="hr-mb-3">
              <label class="hr-form-label">Job Description <span class="required">*</span></label>
              <textarea 
                class="hr-textarea" 
                v-model="formData.jobDescription" 
                rows="4" 
                placeholder="Enter detailed job description"
                required
              ></textarea>
            </div>

            <!-- Requirements -->
            <div class="section-title">Requirements</div>
            <div class="hr-form-grid hr-mb-3">
              <div>
                <label class="hr-form-label">Minimum Education Requirements <span class="required">*</span></label>
                <select class="hr-select" v-model="formData.minEducation" required>
                  <option value="">Select Education Level</option>
                  <option v-for="level in educationLevels" :key="level" :value="level">
                    {{ level }}
                  </option>
                </select>
              </div>
              <div>
                <label class="hr-form-label">Years of Experience <span class="required">*</span></label>
                <select class="hr-select" v-model="formData.yearsExperience" required>
                  <option value="">Select Experience</option>
                  <option v-for="year in experienceYears" :key="year" :value="year">
                    {{ year }} year<span v-if="year !== 1">s</span> and above
                  </option>
                </select>
              </div>
            </div>

            <div class="hr-mb-3">
              <label class="hr-form-label">Professional Certificates Required</label>
              <textarea 
                class="hr-textarea" 
                v-model="formData.professionalCerts" 
                rows="2" 
                placeholder="List required certificates (comma separated). Example: CPA, HRM, PMP, etc."
              ></textarea>
            </div>

            <div class="hr-mb-3">
              <label class="hr-form-label">Specific Skills Required</label>
              <textarea 
                class="hr-textarea" 
                v-model="formData.specificSkills" 
                rows="2" 
                placeholder="List required skills (comma separated). Example: Proficiency in Excel, Project Management, etc."
              ></textarea>
            </div>

            <!-- Vacancy Details -->
            <div class="section-title">Vacancy Details</div>
            <div class="hr-form-grid hr-mb-3">
              <div>
                <label class="hr-form-label">Number of Positions <span class="required">*</span></label>
                <input
                  type="number"
                  min="1"
                  class="hr-input"
                  v-model.number="formData.numPositions"
                  required
                />
              </div>
              <div>
                <label class="hr-form-label">Application Deadline <span class="required">*</span></label>
                <input
                  type="date"
                  class="hr-input"
                  v-model="formData.applicationDeadline"
                  required
                />
              </div>
            </div>

            <div class="hr-form-grid hr-mb-3">
              <div>
                <label class="hr-form-label">Salary Scale <span class="required">*</span></label>
                <select class="hr-select" v-model="formData.salaryScale" required>
                  <option value="">Select Salary Scale</option>
                  <option v-for="scale in salaryScales" :key="scale" :value="scale">
                    {{ scale }}
                  </option>
                </select>
              </div>
              <div>
                <label class="hr-form-label">Status</label>
                <select class="hr-select" v-model="formData.status">
                  <option value="active">Active</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
            </div>

            <!-- Approval -->
            <div class="section-title">Approval</div>
            <div class="hr-form-grid">
              <div>
                <label class="hr-form-label">Approving Authority <span class="required">*</span></label>
                <input
                  type="text"
                  class="hr-input"
                  v-model="formData.approvingAuthority"
                  required
                  placeholder="Enter Approving Authority"
                />
              </div>
              <div>
                <label class="hr-form-label">Approval Date <span class="required">*</span></label>
                <input
                  type="date"
                  class="hr-input"
                  v-model="formData.approvalDate"
                  required
                />
              </div>
            </div>
          </form>
        </template>
        <template #footer>
          <button class="hr-btn hr-btn-secondary" @click="$refs.formModal.close()">Cancel</button>
          <button class="hr-btn hr-btn-success" :disabled="saving" @click="saveVacancy">
            <span v-if="saving" class="hr-spinner-inline"></span>
            {{ isEditing ? 'Update' : 'Save' }}
          </button>
        </template>
      </ModalWrapper>

      <!-- Add Department Modal -->
      <ModalWrapper ref="deptModal" title="Add New Department" size="small">
        <template #body>
          <form @submit.prevent="saveDepartment" class="department-form">
            <div class="hr-mb-3">
              <label class="hr-form-label">Department Name <span class="required">*</span></label>
              <input 
                type="text" 
                class="hr-input" 
                v-model="newDepartment.name" 
                required 
                placeholder="e.g. Human Resources"
              />
            </div>
            <div class="hr-mb-3">
              <label class="hr-form-label">Location</label>
              <input 
                type="text" 
                class="hr-input" 
                v-model="newDepartment.location" 
                placeholder="e.g. Nairobi Office"
              />
            </div>
            <div class="hr-mb-3">
              <label class="hr-form-label">Manager</label>
              <input 
                type="text" 
                class="hr-input" 
                v-model="newDepartment.manager" 
                placeholder="e.g. John Doe"
              />
            </div>
            <div>
              <label class="hr-form-label">Description</label>
              <textarea 
                class="hr-textarea" 
                v-model="newDepartment.description" 
                rows="2" 
                placeholder="Brief description of the department..."
              ></textarea>
            </div>
          </form>
        </template>
        <template #footer>
          <button class="hr-btn hr-btn-secondary" @click="$refs.deptModal.close()">Cancel</button>
          <button class="hr-btn hr-btn-success" :disabled="deptSaving" @click="saveDepartment">
            <span v-if="deptSaving" class="hr-spinner-inline"></span>
            Add Department
          </button>
        </template>
      </ModalWrapper>

      <!-- Candidates Modal -->
      <ModalWrapper ref="candidatesModal" title="Candidates" size="large">
        <template #body>
          <CandidateList :vacancy="selectedVacancy" />
        </template>
      </ModalWrapper>

      <!-- Delete Confirmation Modal -->
      <ModalWrapper ref="deleteModal" title="Confirm Delete" size="small">
        <template #body>
          <div class="text-center">
            <i class="bx bx-error-circle delete-icon"></i>
            <h5>Are you sure?</h5>
            <p class="hr-text-muted">
              You are about to delete <strong>{{ deleteItem?.title }}</strong>. 
              This action cannot be undone.
            </p>
          </div>
        </template>
        <template #footer>
          <button class="hr-btn hr-btn-secondary" @click="$refs.deleteModal.close()">Cancel</button>
          <button class="hr-btn hr-btn-danger" @click="deleteVacancy">Yes, Delete</button>
        </template>
      </ModalWrapper>
    </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import ModalWrapper from "@/components/hrActions/ModalWrapper.vue";
import CandidateList from "./CandidateList.vue";
import { authUrl, execute, toast, getRight } from "@/api";
import { downloadCsv, downloadCountyTablePdf } from "@/utils/countyReportExport";

export default {
  name: "VacancyManagement",
  components: { HrPageLayout, ModalWrapper, CandidateList },
  
  data() {
    return {
      // UI states
      loading: false,
      saving: false,
      deptSaving: false,
      
      // Filters
      searchValue: '',
      statusFilter: '',
      page: 1,
      pageSize: 15,
      totalRecords: 0,
      totalPages: 0,
      
      // Data
      vacancies: [],
      departments: [],
      positions: [],
      units: [],
      vacancySections: [],
      vacancyUnits: [],
      vacancySubUnits: [],
      
      // Form data
      isEditing: false,
      formData: { 
        id: null,
        // Job details
        title: '',
        jobLocation: '',
        department: '',
        sectionId: '',
        unitId: '',
        subUnitId: '',
        vacancyRefNo: '',
        advertDate: '',
        jobDescription: '',
        // Requirements
        minEducation: '',
        yearsExperience: '',
        professionalCerts: '',
        specificSkills: '',
        // Vacancy details
        numPositions: 1,
        applicationDeadline: '',
        salaryScale: '',
        // Meta
        positionId: '',
        status: 'active',
        approvingAuthority: '',
        approvalDate: ''
      },

      educationLevels: [
        'Certificate',
        'Diploma',
        'Higher Diploma',
        'Bachelor\'s Degree',
        'Master\'s Degree',
        'PhD'
      ],
      experienceYears: [0,1,2,3,4,5,7,10,15,20],
      salaryScales: [
        'Scale 1',
        'Scale 2',
        'Scale 3',
        'Scale 4',
        'Scale 5'
      ],
      
      // New department form
      newDepartment: { 
        name: '', 
        location: '', 
        manager: '', 
        description: '' 
      },
      
      // Selected items
      deleteItem: null,
      selectedVacancy: null
    };
  },
  
  mounted() {
    this.fetchVacancies();
    this.fetchDepartments();
    this.fetchPositions();
  },

  methods: {
    // Permission check
    hasRight(role) { 
      return getRight(role); 
    },

    // Fetch positions for dropdown
    async fetchPositions() {
      const data = new FormData();
      data.append('function', 'getAllPositions');
      try {
        const res = await execute(data, authUrl);
        if (res.data && res.data.success) {
          this.positions = res.data.data?.positions || res.data.positions || [];
        }
      } catch (e) {
        console.error('Failed to load positions', e);
      }
    },
    
    // Fetch departments for dropdown
    async fetchDepartments() {
      const data = new FormData();
      data.append('function', 'getDepartments');
      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.departments = res.data.data.departments || [];
        }
      } catch (e) {
        console.error('Failed to load departments', e);
        toast.error('Failed to load departments');
      }
    },
    
    // Fetch units/directorates from database (hr_units via Departments → Section / Sub-votes → Units)
    async fetchUnits() {
      const data = new FormData();
      data.append('function', 'getUnitsForVacancies');
      try {
        const res = await execute(data, authUrl);
        if (res.data && res.data.success) {
          const raw = res.data.data?.units || res.data.units || [];
          this.units = (Array.isArray(raw) ? raw : [raw]).map(u => ({
            ...u,
            displayName: u.displayName || `${u.name}${u.departmentName ? ' - ' + u.departmentName : ''}`
          }));
        }
      } catch (e) {
        console.error('Failed to load units', e);
        toast.error('Failed to load units/directorates');
      }
    },
    
    onVacancyDepartmentChange() {
      this.formData.sectionId = '';
      this.formData.unitId = '';
      this.formData.subUnitId = '';
      this.vacancyUnits = [];
      this.vacancySubUnits = [];
      if (!this.formData.department) {
        this.vacancySections = [];
        return;
      }
      const dept = this.departments.find(d => (d.departmentName || '').trim() === (this.formData.department || '').trim());
      if (dept && dept.id) this.loadVacancySections(dept.id);
      else this.vacancySections = [];
    },
    onVacancySectionChange() {
      this.formData.unitId = '';
      this.formData.subUnitId = '';
      this.vacancySubUnits = [];
      if (!this.formData.sectionId) {
        this.vacancyUnits = [];
        return;
      }
      this.loadVacancyUnits(this.formData.sectionId);
    },
    onVacancyUnitChange() {
      this.formData.subUnitId = '';
      if (!this.formData.unitId) {
        this.vacancySubUnits = [];
        return;
      }
      this.loadVacancySubUnits(this.formData.unitId);
    },
    async loadVacancySections(departmentId) {
      this.vacancySections = [];
      const data = new FormData();
      data.append('function', 'getSectionsByDepartment');
      data.append('departmentId', departmentId);
      try {
        const res = await execute(data, authUrl);
        const list = res?.data?.sections ?? res?.data?.data?.sections ?? [];
        this.vacancySections = Array.isArray(list) ? list : [];
      } catch (e) {
        console.error('Load sections failed', e);
      }
    },
    async loadVacancyUnits(sectionId) {
      this.vacancyUnits = [];
      const data = new FormData();
      data.append('function', 'getUnitsBySection');
      data.append('sectionId', sectionId);
      try {
        const res = await execute(data, authUrl);
        const list = res?.data?.units ?? res?.data?.data?.units ?? [];
        this.vacancyUnits = Array.isArray(list) ? list : [];
      } catch (e) {
        console.error('Load units failed', e);
      }
    },
    async loadVacancySubUnits(unitId) {
      this.vacancySubUnits = [];
      const data = new FormData();
      data.append('function', 'getSubUnitsByUnit');
      data.append('unitId', unitId);
      try {
        const res = await execute(data, authUrl);
        const list = res?.data?.subUnits ?? res?.data?.data?.subUnits ?? [];
        this.vacancySubUnits = Array.isArray(list) ? list : [];
      } catch (e) {
        console.error('Load sub-units failed', e);
      }
    },
    // Open add vacancy modal
    openAddModal() {
      this.isEditing = false;
      this.vacancySections = [];
      this.vacancyUnits = [];
      this.vacancySubUnits = [];
      this.formData = { 
        id: null,
        title: '',
        jobLocation: '',
        department: '',
        sectionId: '',
        unitId: '',
        subUnitId: '',
        vacancyRefNo: '',
        advertDate: '',
        jobDescription: '',
        minEducation: '',
        yearsExperience: '',
        professionalCerts: '',
        specificSkills: '',
        numPositions: 1,
        applicationDeadline: '',
        salaryScale: '',
        positionId: '',
        status: 'active',
        approvingAuthority: '',
        approvalDate: ''
      };
      this.$nextTick(() => this.$refs.formModal?.open());
    },
    
    // Open edit vacancy modal
    async openEditModal(item) {
      this.isEditing = true;
      this.formData = {
        id: item.id,
        title: item.title || '',
        jobLocation: item.job_location || item.jobLocation || '',
        department: item.department_name || item.department || '',
        sectionId: item.section_id || item.sectionId || '',
        unitId: item.unit_id || item.unitId || '',
        subUnitId: item.sub_unit_id || item.subUnitId || '',
        vacancyRefNo: item.vacancy_ref_no || item.vacancyRefNo || '',
        advertDate: item.advert_date || item.advertDate || '',
        jobDescription: item.job_description || item.jobDescription || item.summary || '',
        minEducation: item.min_education || item.minEducation || '',
        yearsExperience: item.years_experience || item.yearsExperience || '',
        professionalCerts: item.professional_certs || item.professionalCerts || '',
        specificSkills: item.specific_skills || item.specificSkills || '',
        numPositions: item.num_positions || item.numPositions || 1,
        applicationDeadline: item.application_deadline || item.applicationDeadline || item.closing_date || item.closingDate || '',
        salaryScale: item.salary_scale || item.salaryScale || '',
        positionId: item.position_id || item.positionId || '',
        status: item.status || 'active',
        approvingAuthority: item.approving_authority || item.approvingAuthority || '',
        approvalDate: item.approval_date || item.approvalDate || ''
      };
      this.vacancySections = [];
      this.vacancyUnits = [];
      this.vacancySubUnits = [];
      const dept = this.departments.find(d => (d.departmentName || '').trim() === (this.formData.department || '').trim());
      if (dept && dept.id) await this.loadVacancySections(dept.id);
      if (this.formData.sectionId) await this.loadVacancyUnits(this.formData.sectionId);
      if (this.formData.unitId) await this.loadVacancySubUnits(this.formData.unitId);
      this.$nextTick(() => this.$refs.formModal?.open());
    },
    
    // Open add department modal
    openAddDepartmentModal() {
      this.newDepartment = { 
        name: '', 
        location: '', 
        manager: '', 
        description: '' 
      };
      this.$nextTick(() => this.$refs.deptModal?.open());
    },
    
    // Save new department
    async saveDepartment() {
      if (!this.newDepartment.name) {
        return toast.error('Department name is required');
      }
      
      this.deptSaving = true;
      const data = new FormData();
      data.append('function', 'addDepartment');
      data.append('name', this.newDepartment.name);
      data.append('location', this.newDepartment.location || '');
      data.append('manager', this.newDepartment.manager || '');
      data.append('description', this.newDepartment.description || '');
      
      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.$refs.deptModal?.close();
          await this.fetchDepartments(); // Refresh departments list
          
          // Auto-select the newly created department
          this.formData.department = this.newDepartment.name;
          
          toast.success('Department added successfully');
        } else {
          toast.error(res.data.message || 'Failed to add department');
        }
      } catch (e) {
        console.error('Save department error:', e);
        toast.error('Failed to add department');
      } finally {
        this.deptSaving = false;
      }
    },
    
    // View candidates for a vacancy
    viewCandidates(item) {
      this.selectedVacancy = item;
      this.$nextTick(() => this.$refs.candidatesModal?.open());
    },
    
    // Fetch vacancies with filters
    async fetchVacancies() {
      this.loading = true;
      const data = new FormData();
      data.append('function', 'getVacancies');
      data.append('search', this.searchValue);
      data.append('status', this.statusFilter);
      data.append('page', String(this.page));
      data.append('pageSize', String(this.pageSize));

      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          const raw = res.data.data.Records || res.data.data.vacancies || [];
          const pag = res.data.data.pagination || {};
          this.page = pag.page || this.page;
          this.pageSize = pag.pageSize || this.pageSize;
          this.totalRecords = pag.total || 0;
          this.totalPages = pag.totalPages || 1;

          this.vacancies = (Array.isArray(raw) ? raw : [raw]).map(v => ({
            ...v,
            closingDate: v.closing_date || v.closingDate || '',
            department: v.department_name || v.department || '',
            statusNormalized: String(v.status || v.state || '').toLowerCase()
          }));
        } else {
          toast.error(res.data.message || 'Failed to load vacancies');
        }
      } catch (e) {
        console.error('Fetch vacancies error:', e);
        toast.error('Failed to load vacancies');
      } finally {
        this.loading = false;
      }
    },

    async closeVacancy(v) {
      if (!confirm(`Close vacancy "${v.title}"?`)) return;
      const fd = new FormData();
      fd.append('function', 'closeVacancy');
      fd.append('id', v.id);
      const res = await execute(fd, authUrl);
      if (res.data?.success) {
        toast.success('Vacancy closed');
        this.fetchVacancies();
      } else toast.error(res.data?.message || 'Failed to close');
    },

    async readvertiseVacancy(v) {
      const fd = new FormData();
      fd.append('function', 'readvertiseVacancy');
      fd.append('id', v.id);
      const res = await execute(fd, authUrl);
      if (res.data?.success) {
        toast.success('Vacancy is active again');
        this.fetchVacancies();
      } else toast.error(res.data?.message || 'Failed to re-advertise');
    },

    async copyShareLink(v) {
      const fd = new FormData();
      fd.append('function', 'getVacancyShareLink');
      fd.append('id', v.id);
      try {
        const res = await execute(fd, authUrl);
        if (!res.data?.success) return toast.error(res.data?.message || 'Could not create link');
        const url = res.data.data.url;
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(url);
          toast.success('Share link copied to clipboard');
        } else {
          window.prompt('Copy share link:', url);
        }
      } catch (e) {
        toast.error('Could not create share link');
      }
    },

    async exportVacancyReport() {
      const fd = new FormData();
      fd.append('function', 'getVacancyReports');
      fd.append('reportType', 'status');
      fd.append('search', this.searchValue);
      fd.append('status', this.statusFilter);
      const res = await execute(fd, authUrl);
      const rows = res.data?.data?.rows || [];
      downloadCsv(
        ['Title', 'Ref', 'Status', 'Department', 'Location', 'Deadline', 'Positions', 'Applicants', 'Shortlisted'],
        rows.map((r) => [
          r.title, r.vacancy_ref_no, r.status, r.department_name, r.job_location,
          r.application_deadline, r.num_positions, r.applicants_count, r.shortlisted_count
        ]),
        `vacancies_${Date.now()}.csv`
      );
      toast.success('CSV report downloaded');
    },

    async exportVacancyPdf() {
      const fd = new FormData();
      fd.append('function', 'getVacancyReports');
      fd.append('reportType', 'status');
      fd.append('search', this.searchValue);
      fd.append('status', this.statusFilter);
      const res = await execute(fd, authUrl);
      const rows = res.data?.data?.rows || [];
      await downloadCountyTablePdf({
        title: 'VACANCY MANAGEMENT REPORT',
        subtitle: 'Public Service Board',
        headers: ['Title', 'Ref', 'Status', 'Dept', 'Deadline', 'Applicants'],
        rows: rows.map((r) => [
          r.title, r.vacancy_ref_no, r.status, r.department_name, r.application_deadline, r.applicants_count
        ]),
        filename: `vacancies_${Date.now()}.pdf`,
        orientation: 'landscape'
      });
      toast.success('PDF report generated');
    },
    
    // Save vacancy (add or update)
    async saveVacancy() {
      if (!this.formData.title) {
        return this.showError('Job title is required');
      }
      if (!this.formData.jobLocation) {
        return this.showError('Job location is required');
      }
      if (!this.formData.department) {
        return this.showError('Department is required');
      }
      if (!this.formData.vacancyRefNo) {
        return this.showError('Vacancy reference number is required');
      }
      if (!this.formData.advertDate) {
        return this.showError('Advert date is required');
      }
      if (!this.formData.jobDescription) {
        return this.showError('Job description is required');
      }
      if (!this.formData.minEducation) {
        return this.showError('Minimum education is required');
      }
      if (!this.formData.yearsExperience && this.formData.yearsExperience !== 0) {
        return this.showError('Years of experience is required');
      }
      if (!this.formData.numPositions || this.formData.numPositions < 1) {
        return this.showError('Number of positions is required');
      }
      if (!this.formData.applicationDeadline) {
        return this.showError('Application deadline is required');
      }
      if (!this.formData.salaryScale) {
        return this.showError('Salary scale is required');
      }
      if (!this.formData.approvingAuthority) {
        return this.showError('Approving authority is required');
      }
      if (!this.formData.approvalDate) {
        return this.showError('Approval date is required');
      }
      
      this.saving = true;
      const data = new FormData();
      data.append('function', this.isEditing ? 'updateVacancy' : 'addVacancy');
      
      if (this.isEditing) {
        data.append('id', this.formData.id);
      }
      
      data.append('title', this.formData.title);
      data.append('jobLocation', this.formData.jobLocation || '');
      data.append('vacancyRefNo', this.formData.vacancyRefNo || '');
      data.append('advertDate', this.formData.advertDate || '');
      data.append('jobDescription', this.formData.jobDescription || '');
      data.append('minEducation', this.formData.minEducation || '');
      data.append('yearsExperience', this.formData.yearsExperience !== '' ? this.formData.yearsExperience : '');
      data.append('professionalCerts', this.formData.professionalCerts || '');
      data.append('specificSkills', this.formData.specificSkills || '');
      data.append('numPositions', this.formData.numPositions || 1);
      data.append('applicationDeadline', this.formData.applicationDeadline || '');
      data.append('salaryScale', this.formData.salaryScale || '');
      data.append('status', this.formData.status || 'active');
      data.append('positionId', this.formData.positionId || '');
      data.append('sectionId', this.formData.sectionId || '');
      data.append('unitId', this.formData.unitId || '');
      data.append('subUnitId', this.formData.subUnitId || '');
      data.append('approvingAuthority', this.formData.approvingAuthority || '');
      data.append('approvalDate', this.formData.approvalDate || '');
      
      // Convert department name to ID
      if (this.formData.department) {
        const dept = this.departments.find(d => d.departmentName === this.formData.department);
        if (dept) {
          data.append('departmentId', dept.id);
        }
      }
      
      // Add created by if needed
      // data.append('createdBy', this.$store.state.user?.name || 'System');
      
      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.$refs.formModal?.close();

          // If this was a newly created vacancy and no existing position selected,
          // also create a corresponding position
          if (!this.isEditing && !this.formData.positionId) {
            try {
              const dept = this.departments.find(d => d.departmentName === (this.formData.department || ''));
              const deptId = dept ? dept.id : '';

              const posData = new FormData();
              posData.append('function', 'createPosition');
              // generate a simple code from title
              const code = (this.formData.title || '').toUpperCase().replace(/[^A-Z0-9]+/g, '_').slice(0,30) || `VAC_${Date.now()}`;
              posData.append('code', code);
              posData.append('name', this.formData.title || '');
              posData.append('description', this.formData.summary || '');
              if (deptId) posData.append('departmentId', deptId);
              posData.append('status', this.formData.status || 'active');

              // Call backend to create position (ignore failure but log)
              const posRes = await execute(posData, authUrl);
              if (posRes.data && posRes.data.success) {
                toast.success('Position created for new vacancy');
              } else {
                console.warn('Position creation failed', posRes.data || posRes);
              }
            } catch (e) {
              console.error('Create position error:', e);
            }
          }

          await this.fetchVacancies(); // Refresh the list
          toast.success(this.isEditing ? 'Vacancy updated successfully' : 'Vacancy created successfully');
        } else {
          this.showError(res.data.message || 'Failed to save vacancy');
        }
      } catch (e) {
        console.error('Save vacancy error:', e);
        const msg = e?.response?.data?.message || e?.message || 'Failed to save vacancy';
        this.showError(msg);
      } finally {
        this.saving = false;
      }
    },
    
    // Confirm delete
    confirmDelete(item) {
      this.deleteItem = item;
      this.$nextTick(() => this.$refs.deleteModal?.open());
    },
    
    // Delete vacancy
    async deleteVacancy() {
      if (!this.deleteItem) return;
      
      const data = new FormData();
      data.append('function', 'deleteVacancy');
      data.append('id', this.deleteItem.id);
      
      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.$refs.deleteModal?.close();
          await this.fetchVacancies(); // Refresh the list
          this.deleteItem = null;
          toast.success('Vacancy deleted successfully');
        } else {
          this.showError(res.data.message || 'Failed to delete vacancy');
        }
      } catch (e) {
        console.error('Delete vacancy error:', e);
        this.showError('Failed to delete vacancy');
      }
    },
    
    // Error handler
    showError(msg) { 
      console.error(msg); 
      toast.error(String(msg || 'An error occurred')); 
    }
  }
};
</script>

<style scoped>
.type-name { 
  font-weight: 600; 
  font-size: 14px; 
}

.type-desc { 
  font-size: 12px; 
  color: #64748b; 
  display: block; 
  max-width: 300px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.vacancies-content { 
  padding-top: 12px; 
}
.vac-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 14px 0 4px;
  font-size: 13px;
  color: #475569;
}

.hr-table td, 
.hr-table th { 
  vertical-align: middle; 
}

.hr-form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.required {
  color: #dc2626;
  margin-left: 4px;
}

.delete-icon {
  font-size: 48px;
  color: #dc2626;
  margin-bottom: 16px;
}

/* Modal form styles */
.vacancy-form,
.department-form {
  padding: 8px 0;
}

/* Button hover effects */
.hr-btn-icon:hover {
  transform: scale(1.05);
  transition: transform 0.2s;
}

/* Department dropdown button */
.hr-btn-sm {
  height: 38px;
  min-width: 38px;
}

/* Loading state */
.hr-spinner-inline {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: 8px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .hr-form-grid {
    grid-template-columns: 1fr;
  }
  
  .type-desc {
    max-width: 200px;
  }
}
</style>